import { POLYGON_CHAIN_ID, RELAYER_FALLBACK_URL, REMOTE_SIGNING_URL } from '@/constants/polymarket'
import { RelayClient, type SafeTransaction } from '@polymarket/builder-relayer-client'
import { BuilderConfig } from '@polymarket/builder-signing-sdk'

/**
 * Check if an error is a 429 Too Many Requests from the relayer.
 * The relayer client throws errors like:
 *   Error: {"error":"request error","status":429,"statusText":"","data":{...}}
 */
export function isRateLimitError(err: unknown): boolean {
	if (err instanceof Error) {
		const msg = err.message.toLowerCase()
		if (
			msg.includes('429') ||
			msg.includes('too many requests') ||
			msg.includes('rate limit') ||
			msg.includes('quota exceeded')
		) {
			return true
		}
	}
	// Handle cases where the error object has a status property directly
	if (
		err &&
		typeof err === 'object' &&
		'status' in err &&
		(err as { status: number }).status === 429
	) {
		return true
	}
	return false
}

/**
 * Execute relay transactions with automatic fallback to the backup relayer on 429.
 *
 * Tries the provided relayClient first. If it returns 429, creates a new
 * RelayClient pointing at RELAYER_FALLBACK_URL and retries the same batch.
 */
export async function executeWithRelayerFallback(
	relayClient: RelayClient,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	walletClient: any,
	txs: SafeTransaction[],
	description: string
) {
	try {
		return await relayClient.execute(txs, description)
	} catch (err) {
		if (isRateLimitError(err)) {
			console.warn(
				'Relayer returned 429 during execute, retrying with fallback:',
				RELAYER_FALLBACK_URL
			)
			const builderConfig = new BuilderConfig({
				remoteBuilderConfig: { url: REMOTE_SIGNING_URL() },
			})
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const fallbackClient = new RelayClient(
				RELAYER_FALLBACK_URL,
				POLYGON_CHAIN_ID,
				walletClient as any,
				builderConfig
			)
			return fallbackClient.execute(txs, description)
		}
		throw err
	}
}
